/**
 * WorkSelector Component
 * Reusable work search and selection with server-side search, portaled dropdown
 * Used in EstimateForm, DesignForm, MeasurementsPage, etc.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import { worksListApi } from '../services/wmsService';
import { useWorkMasterList } from '../hooks/useWorkMasterList';

export interface SelectedWork {
  wrk_id: number;
  wrk_cd: string;
  wrk_nm: string;
  wrk_sts_nm?: string;
  wrk_type_nm?: string;
  wrk_ctgry_nm?: string;
  ulb_nm?: string;
  // Scheme and contractor were absent from this type, so WorkDetailsCard's Scheme and
  // Contractor fields had nowhere to read from even when the caller had the values.
  schme_nm?: string;
  cntrctr_nm?: string;
  snctn_at?: number;
  orgnl_estmt_at?: number;
}

interface WorkSelectorProps {
  selectedWork: SelectedWork | null;
  onSelect: (work: SelectedWork) => void;
  onClear: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Extra server-side filter params (spread into every search request).
   * Handy flags:
   *   - withoutDesign:   only works that currently have no active design
   *   - withoutEstimate: only works that currently have no active estimate
   */
  extraFilters?: Record<string, any>;
  /**
   * Shown when the server returns no works and the user has typed nothing — i.e.
   * `extraFilters` alone excluded everything there is to pick.
   *
   * Without this the dropdown fell back to "Type to search works", which is a
   * lie in that state: the list is already empty on its own terms and no amount
   * of typing can add to it. Callers that pass a narrowing flag should say what
   * the flag ruled out, because only the caller knows.
   */
  emptyFilteredMessage?: string;
  /**
   * A work to drop from the results — the one the caller is already editing.
   * "Import from Work" offered the current work as a source, where copying its
   * schedule onto itself does nothing.
   */
  excludeWorkId?: number | null;
}

export default function WorkSelector({
  selectedWork, onSelect, onClear,
  label = 'Select Work',
  placeholder = 'Search and select a work...',
  disabled = false,
  extraFilters,
  emptyFilteredMessage,
  excludeWorkId,
}: WorkSelectorProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  // Whether a search response has come back at least once since the dropdown was
  // opened. "No results yet" and "the server said there are none" render the same
  // empty array, so without this the two are indistinguishable and the empty state
  // has to guess — which is how "Type to search works" ended up showing for a list
  // that was already conclusively empty.
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [showFilters, setShowFilters] = useState(true);
  // Event tags (e.g. Pushkaralu) — event-tagged works are hidden from the default
  // list, so an explicit Event selection is needed to surface them here.
  const { eventTags } = useWorkMasterList();
  const [filters, setFilters] = useState({ workType: '', workCategory: '', financialYear: '', udaId: '', event: '' });
  const [filterOptions, setFilterOptions] = useState<any>({ financialYears: [], ulbs: [], sectors: [], categories: [] });
  const [ulbSearch, setUlbSearch] = useState('');
  const [ulbDropdownOpen, setUlbDropdownOpen] = useState(false);
  const ulbBtnRef = useRef<HTMLButtonElement>(null);
  const [ulbDropdownPos, setUlbDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load filter options once
  useEffect(() => {
    if (!dropdownOpen) return;
    (async () => {
      try {
        const opts = await worksListApi.getFilterOptions();
        console.log('WorkSelector filter options loaded:', opts);
        setFilterOptions(opts || {});
      } catch (e) { console.error('WorkSelector filter options error:', e); }
    })();
  }, [dropdownOpen]);

  // Debounced server-side search
  useEffect(() => {
    if (!dropdownOpen) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Newest-first by default so a just-created/converted work surfaces at the top.
        const params: any = { orderBy: 'w.wrk_id', orderDirection: 'DESC', ...(extraFilters || {}), search: search || undefined };
        if (filters.workType) params.workType = filters.workType;
        if (filters.workCategory) params.workCategory = filters.workCategory;
        if (filters.financialYear) params.financialYear = filters.financialYear;
        if (filters.udaId) params.udaId = filters.udaId;
        // When an Event is selected, surface the event-tagged (Pushkaralu) works
        // that are otherwise excluded from the default list.
        if (filters.event) params.onlyPushkaralu = true;
        const result = await worksListApi.getAll(params, 1, 15);
        // Dropped here rather than server-side: it is one row out of a page of 15, and
        // the works-list endpoint has no "everything but this id" filter worth adding.
        setResults((result.data || []).filter(
          (w: any) => !excludeWorkId || Number(w.wrk_id) !== Number(excludeWorkId)));
        setError(false);
      } catch (e) {
        console.error('Work search failed:', e);
        // A failed request must NOT leave the previous rows on screen, and must not
        // fall through to the "nothing matches this filter" message — that would
        // report a server fault as a business fact.
        setResults([]);
        setError(true);
      }
      finally { setLoading(false); setFetched(true); }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [search, dropdownOpen, filters, extraFilters, excludeWorkId]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 500) });
    }
    setDropdownOpen(true);
    setSearch('');
    setResults([]);
    setFetched(false);
    setError(false);
  }, [disabled]);

  const hasLocalFilters = Object.values(filters).some(Boolean);
  const clearLocalFilters = () =>
    setFilters({ workType: '', workCategory: '', financialYear: '', udaId: '', event: '' });

  const handleSelect = (work: any) => {
    // Pass the whole work through. This used to rebuild a fixed nine-key object, so any
    // field the search returned under another name -- or any field not on the list at
    // all, schme_nm among them -- was silently dropped between the dropdown and the
    // consumer. That is why a bill header could show the sanction amount (snctn_at was
    // on the list) while Work Type, Category, UDA and Scheme all rendered as dashes.
    //
    // The explicit keys stay after the spread so the SelectedWork contract is still
    // satisfied even when the search response is shaped differently.
    onSelect({
      ...work,
      wrk_id: work.wrk_id,
      wrk_cd: work.wrk_cd,
      wrk_nm: work.wrk_nm,
      wrk_sts_nm: work.wrk_sts_nm ?? work.workStatus,
      wrk_type_nm: work.wrk_type_nm ?? work.wrk_type ?? work.workType,
      wrk_ctgry_nm: work.wrk_ctgry_nm ?? work.workCategory,
      ulb_nm: work.ulb_nm ?? work.uda_nm ?? work.uda_name ?? work.udaName,
      schme_nm: work.schme_nm ?? work.scheme_name ?? work.schemeName,
      cntrctr_nm: work.cntrctr_nm ?? work.contractorName,
      snctn_at: work.snctn_at,
      orgnl_estmt_at: work.orgnl_estmt_at,
    });
    setDropdownOpen(false);
  };

  return (
    <div>
      {label && <label className="text-xs font-semibold text-gray-700 block mb-1.5">{label}</label>}

      {selectedWork ? (
        <div className="flex items-center gap-3 bg-white border-2 border-blue-300 rounded-lg p-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-building-4-line text-blue-600"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-blue-700">{selectedWork.wrk_cd}</span>
              {selectedWork.wrk_sts_nm && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{selectedWork.wrk_sts_nm}</span>}
            </div>
            <p className="text-sm text-gray-800 truncate">{selectedWork.wrk_nm}</p>
            <div className="flex items-center gap-3 mt-0.5">
              {selectedWork.wrk_type_nm && <span className="text-[10px] text-gray-500">{selectedWork.wrk_type_nm}</span>}
              {selectedWork.wrk_ctgry_nm && <span className="text-[10px] text-gray-400">| {selectedWork.wrk_ctgry_nm}</span>}
              {selectedWork.ulb_nm && <span className="text-[10px] text-gray-400">| {selectedWork.ulb_nm}</span>}
            </div>
          </div>
          {!disabled && (
            <button onClick={onClear} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Change work">
              <i className="ri-close-circle-line text-lg"></i>
            </button>
          )}
        </div>
      ) : (
        <div ref={btnRef}>
          <button type="button" onClick={openDropdown} disabled={disabled}
            className="w-full border-2 border-dashed border-blue-300 rounded-lg px-4 py-3 text-left hover:border-blue-400 hover:bg-blue-50/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <i className="ri-search-line text-blue-400"></i>
            <span className="text-sm text-blue-500">{placeholder}</span>
          </button>
        </div>
      )}

      {/* Portaled dropdown — sits above modals (bulk-setup forms use z-[10000]) */}
      {dropdownOpen && createPortal(
        <>
          <div className="fixed inset-0" style={{ zIndex: 10010 }} onClick={() => setDropdownOpen(false)}></div>
          <div className="fixed bg-white border rounded-xl shadow-xl max-h-96 flex flex-col" style={{ zIndex: 10011, top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
            <div className="p-3 border-b">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by work code, name, UDA..." className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm" autoFocus />
                </div>
                <button onClick={() => setShowFilters(!showFilters)}
                  className={`px-2.5 py-2 border rounded-lg text-sm flex items-center gap-1 transition-colors ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-gray-50'}`}
                  title="Toggle filters">
                  <i className="ri-filter-3-line"></i>
                  {Object.values(filters).some(v => v) && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>}
                </button>
              </div>
              {/* Filters */}
              {showFilters && (
                <>
                {(eventTags || []).length > 0 && (
                  <div className="mt-2">
                    <select value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })}
                      className={`w-full border rounded px-2 py-1.5 text-xs ${filters.event ? 'border-amber-400 bg-amber-50 text-amber-800' : ''}`}>
                      <option value="">All Works (event works hidden)</option>
                      {(eventTags || []).map((t: any) => <option key={t.evnt_tag_id} value={String(t.evnt_tag_id)}>{t.evnt_tag_nm} works</option>)}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <select value={filters.financialYear} onChange={(e) => setFilters({ ...filters, financialYear: e.target.value })}
                    className="border rounded px-2 py-1.5 text-xs">
                    <option value="">All Years</option>
                    {(filterOptions.financialYears || []).map((fy: any) => <option key={fy.id || fy.code} value={fy.code}>{fy.name}</option>)}
                  </select>
                  <select value={filters.workType} onChange={(e) => setFilters({ ...filters, workType: e.target.value })}
                    className="border rounded px-2 py-1.5 text-xs">
                    <option value="">All Types</option>
                    {(filterOptions.sectors || []).map((s: any) => <option key={s.id || s.code} value={s.code}>{s.name}</option>)}
                  </select>
                  <select value={filters.workCategory} onChange={(e) => setFilters({ ...filters, workCategory: e.target.value })}
                    className="border rounded px-2 py-1.5 text-xs">
                    <option value="">All Categories</option>
                    {(filterOptions.categories || []).map((c: any) => <option key={c.id || c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <div className="relative">
                    <button ref={ulbBtnRef} type="button" onClick={() => {
                      if (ulbBtnRef.current) {
                        const rect = ulbBtnRef.current.getBoundingClientRect();
                        setUlbDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 220) });
                      }
                      setUlbDropdownOpen(!ulbDropdownOpen); setUlbSearch('');
                    }} className="w-full border rounded px-2 py-1.5 text-xs text-left flex items-center justify-between hover:border-blue-400">
                      <span className={filters.udaId ? 'text-gray-900' : 'text-gray-500'}>
                        {filters.udaId ? (filterOptions.ulbs || []).find((u: any) => String(u.id) === filters.udaId)?.name || 'UDA' : 'All UDAs'}
                      </span>
                      <i className="ri-arrow-down-s-line text-gray-400 text-[10px]"></i>
                    </button>
                    {ulbDropdownOpen && createPortal(
                      <>
                        <div className="fixed inset-0" style={{ zIndex: 99998 }} onClick={() => setUlbDropdownOpen(false)}></div>
                        <div className="fixed bg-white border rounded-lg shadow-xl max-h-52 flex flex-col" style={{ zIndex: 99999, top: ulbDropdownPos.top, left: ulbDropdownPos.left, width: ulbDropdownPos.width }}>
                          <div className="p-1.5 border-b">
                            <div className="relative">
                              <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]"></i>
                              <input type="text" value={ulbSearch} onChange={(e) => setUlbSearch(e.target.value)}
                                placeholder="Search UDA..." className="w-full pl-6 pr-2 py-1 border rounded text-xs" autoFocus />
                            </div>
                          </div>
                          <div className="overflow-y-auto flex-1">
                            <button type="button" onClick={() => { setFilters({ ...filters, udaId: '' }); setUlbDropdownOpen(false); }}
                              className="w-full px-2 py-1.5 text-left text-xs hover:bg-blue-50 border-b border-gray-50 font-medium text-gray-500">All UDAs</button>
                            {(filterOptions.ulbs || [])
                              .filter((u: any) => !ulbSearch || u.name?.toLowerCase().includes(ulbSearch.toLowerCase()))
                              .map((u: any) => (
                                <button key={u.id} type="button" onClick={() => { setFilters({ ...filters, udaId: String(u.id) }); setUlbDropdownOpen(false); }}
                                  className={`w-full px-2 py-1.5 text-left text-xs hover:bg-blue-50 border-b border-gray-50 ${filters.udaId === String(u.id) ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}>
                                  {u.name}
                                </button>
                              ))}
                          </div>
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {loading || !fetched ? (
                /* `!fetched` covers the 300ms debounce before the first request goes
                   out, so the empty state never flashes an answer it does not have. */
                <div className="p-3"><ListSkeleton rows={4} avatar={false} /></div>
              ) : error ? (
                <div className="p-6 text-center">
                  <i className="ri-error-warning-line text-xl text-red-400"></i>
                  <p className="text-sm text-red-600 mt-1">Could not load works</p>
                  <p className="text-xs text-gray-400 mt-0.5">The search request failed. Try again in a moment.</p>
                </div>
              ) : results.length === 0 ? (
                <div className="p-6 text-center">
                  {search ? (
                    <p className="text-sm text-gray-500">No works match &ldquo;{search}&rdquo;</p>
                  ) : (
                    /* Nothing typed and the server still returned nothing: the filters
                       alone emptied the list. Saying "Type to search works" here sends
                       the user to keep typing at a list that can never fill. */
                    <p className="text-sm text-gray-500">{emptyFilteredMessage || 'No works match this filter.'}</p>
                  )}
                  {hasLocalFilters ? (
                    <button type="button" onClick={clearLocalFilters}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline">
                      Clear filters
                    </button>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">
                      Only works in the UDA selected in the header are listed.
                    </p>
                  )}
                </div>
              ) : (
                results.map((w: any) => (
                  <button key={w.wrk_id} type="button" onClick={() => handleSelect(w)}
                    className="w-full px-4 py-2.5 text-left hover:bg-blue-50 border-b border-gray-50 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-blue-700">{w.wrk_cd}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{w.wrk_sts_nm}</span>
                      </div>
                      <p className="text-sm text-gray-800 truncate">{w.wrk_nm}</p>
                      <p className="text-[10px] text-gray-400">{[w.wrk_type_nm, w.wrk_ctgry_nm, w.ulb_nm].filter(Boolean).join(' | ')}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300"></i>
                  </button>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
